import React from 'react';
import { useTranslation } from 'react-i18next';
import DuotoneLightIcon from '../common/DuotoneLightIcon';
import { useOverlay, leftShownStatus } from '../../contexts/OverlayContext';
import { PRIMARY_DARK, getPrimaryDark } from '../../constants/constants';
import { leftRailWidth } from '../../constants/gridConfig';
import { useStore } from '../../store/store';
import { isAVariableBox } from '../../utils/boxes';
import { hexToRgba } from '../../utils/theme';
import { sortAndFilterVariablesGroupedByContainer } from '../../utils/variableOrderArray';
import { useExitFormView } from './useExitFormView';

// Navigation verticale du panel de gauche : un onglet = un shownStatus
// (cf. OverlayContext). Rajouter un onglet plus tard = une entrée ici +
// un cas de rendu dans LeftSide.jsx, rien d'autre à toucher.
// Icônes précalculées ici (pas de nom passé en variable à icon()) : la macro
// FontAwesome exige des string literals, elle ne résout pas un nom dynamique
// passé dans un .map().
const RAIL_ITEMS = [
  {
    status: leftShownStatus.DOCUMENTS,
    labelKey: 'documents',
    icon: <DuotoneLightIcon name='folder-open' style={{ fontSize: 18 }} />,
    // Même accès que le bouton Documents d'origine : admin, ou vendeur sur un
    // document déjà rattaché à un template (offer/opportunity).
    requiresCanSave: true,
  },
  {
    status: leftShownStatus.SUMMARY,
    labelKey: 'summary',
    icon: <DuotoneLightIcon name='bars-staggered' style={{ fontSize: 18 }} />,
  },
  {
    status: leftShownStatus.SECTIONS,
    labelKey: 'rail-sections',
    icon: <DuotoneLightIcon name='folder-tree' style={{ fontSize: 18 }} />,
  },
  {
    status: leftShownStatus.EXPORT,
    labelKey: 'export_pdf_menu',
    icon: <DuotoneLightIcon name='file-pdf' style={{ fontSize: 18 }} />,
  },
  {
    status: leftShownStatus.ELEMENTS,
    labelKey: 'rail-elements',
    icon: <DuotoneLightIcon name='shapes' style={{ fontSize: 18 }} />,
  },
  {
    status: leftShownStatus.LAYOUTS,
    labelKey: 'rail-layouts',
    icon: <DuotoneLightIcon name='table-layout' style={{ fontSize: 18 }} />,
  },
  {
    status: leftShownStatus.VARIABLES,
    labelKey: 'rail-form',
    icon: <DuotoneLightIcon name='list-check' style={{ fontSize: 18 }} />,
  },
  {
    status: leftShownStatus.SETTINGS,
    labelKey: 'rail-settings',
    icon: <DuotoneLightIcon name='gear' style={{ fontSize: 18 }} />,
  },
];

// Style commun des boutons du rail.
// accentColor : couleur du logo pour le mode courant (violet en template,
// navy en proposal — cf. getPrimaryDark), pas une couleur fixe, pour que
// l'état actif du rail suive le logo quel que soit le mode.
const railButtonStyle = (isActive, accentColor) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 4,
  padding: '8px 2px',
  margin: '0 4px',
  border: 'none',
  borderRadius: 8,
  background: isActive ? hexToRgba(accentColor, 0.1) : 'transparent',
  color: isActive ? accentColor : PRIMARY_DARK,
  fontFamily: 'inherit',
  fontSize: 9,
  fontWeight: isActive ? 700 : 500,
  lineHeight: 1.2,
  cursor: 'pointer',
});

const LeftSideRail = ({ variablesGroupedByContainer }) => {
  const { t } = useTranslation();
  const { overlayProps, updateLeftShownStatus } = useOverlay();
  const shownStatus = overlayProps.left.shownStatus;
  const isAdmin = useStore(({ user }) => user?.isAdmin);
  const currentDocument = useStore(({ currentDocument }) => currentDocument);
  const canSave = isAdmin || (currentDocument.type === 'offer' || currentDocument.type === 'opportunity');
  const accentColor = getPrimaryDark(currentDocument.type === 'template');

  const variableOrderArray = useStore((state) => state.variableOrderArray);
  const boxes = useStore(({ boxes }) => boxes);
  const selectedBoxId = useStore(({ selectedBoxId }) => selectedBoxId);
  const exitFormView = useExitFormView();

  // Reprend la logique de l'ex-bouton bascule Document/Formulaire
  // (LeftSideButton) : quitter le formulaire remet le scroll sur la box
  // précédemment sélectionnée (cf. useExitFormView), y entrer cible la box
  // sélectionnée ou la première variable disponible.
  const handleItemClick = (status) => {
    if (status === shownStatus) return;

    if (shownStatus === leftShownStatus.VARIABLES) {
      exitFormView(status);
      return;
    }

    let boxId;
    if (status === leftShownStatus.VARIABLES) {
      const usedVariablesGroupedByContainer = sortAndFilterVariablesGroupedByContainer({
        variablesGroupedByContainer,
        variableOrderArray,
        filterHiddenBoxes: currentDocument.type !== 'template',
      });
      const selectedBox = boxes.find(({ id }) => id === selectedBoxId);
      if (selectedBox && isAVariableBox(selectedBox.type) && !selectedBox.hideInForm) {
        boxId = selectedBox.id;
      } else if (usedVariablesGroupedByContainer.length > 0) {
        boxId = usedVariablesGroupedByContainer[0].variablesBoxes[0].id;
      }
    }

    updateLeftShownStatus(status, boxId);
  };

  return (
    <div style={{
      width: leftRailWidth,
      // Colonne fixe indépendante (cf. LeftSide.jsx) : hauteur explicite,
      // pas de parent flex ici pour un alignSelf stretch.
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      gap: 4,
      paddingTop: 12,
      // Atténuée (rgba) pour rester légère à l'œil.
      borderRight: '1px solid rgba(11, 34, 57, 0.25)',
    }}>
      {RAIL_ITEMS
        .filter((item) => !item.requiresCanSave || canSave)
        .filter((item) => !item.requiresTemplate || currentDocument.type === 'template')
        .map(({ status, labelKey, icon: itemIcon }) => {
          const isActive = shownStatus === status;
          return (
            <button
              key={status}
              onClick={() => handleItemClick(status)}
              style={railButtonStyle(isActive, accentColor)}
            >
              {itemIcon}
              {t(labelKey)}
            </button>
          );
        })}
    </div>
  );
};

export default LeftSideRail;
